'use client';

import React, { useState, createContext, useContext } from 'react';
import {
  CaretDownIcon,
  CaretRightIcon,
  PlusIcon,
  Cross1Icon,
  ArrowDownIcon,
} from '@radix-ui/react-icons';

import { VStack } from '../index';

import { Box, BoxProps } from './box';
import { Icon } from './icon';

// Accordion Context
interface AccordionContextValue {
  multiple: boolean;
  value: string[];
  onValueChange: (value: string[]) => void;
  variant: 'default' | 'bordered' | 'filled' | 'separated';
  size: 'sm' | 'md' | 'lg';
  iconType: 'caret' | 'caret-right' | 'plus' | 'arrow';
  iconPosition: 'left' | 'right';
}

const AccordionContext = createContext<AccordionContextValue | undefined>(undefined);

const useAccordionContext = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error('Accordion compound components must be used within Accordion.Root');
  }
  return context;
};

// Context for passing item value to trigger
const AccordionItemContext = React.createContext<string>('');

// Root Accordion Component
export interface AccordionRootProps extends BoxProps {
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
  multiple?: boolean;
  variant?: 'default' | 'bordered' | 'filled' | 'separated';
  size?: 'sm' | 'md' | 'lg';
  iconType?: 'caret' | 'caret-right' | 'plus' | 'arrow';
  iconPosition?: 'left' | 'right';
  children: React.ReactNode;
}

const AccordionRoot = React.forwardRef<HTMLDivElement, AccordionRootProps>(
  (
    {
      value: controlledValue,
      defaultValue = [],
      onValueChange,
      multiple = true,
      variant = 'default',
      size = 'md',
      iconType = 'caret',
      iconPosition = 'right',
      children,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState<string[]>(controlledValue || defaultValue);
    const value = controlledValue !== undefined ? controlledValue : internalValue;

    const handleValueChange = (newValue: string[]) => {
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    };

    const contextValue: AccordionContextValue = {
      multiple,
      value,
      onValueChange: handleValueChange,
      variant,
      size,
      iconType,
      iconPosition,
    };

    const variantStyles = {
      default: {
        borderRadius: 'none',
        border: 'none',
      },
      bordered: {
        borderRadius: 'lg',
        border: '1px solid #E2E8F0',
      },
      filled: {
        backgroundColor: '#F7FAFC',
        borderRadius: 'lg',
        border: '1px solid #E2E8F0',
      },
      separated: {
        borderRadius: 'none',
        border: 'none',
      },
    };

    return (
      <AccordionContext.Provider value={contextValue}>
        <Box
          ref={ref}
          overflow="hidden"
          {...variantStyles[variant as keyof typeof variantStyles]}
          {...props}
        >
          <VStack gap={variant === 'separated' ? 4 : 0} align="stretch">
            {children}
          </VStack>
        </Box>
      </AccordionContext.Provider>
    );
  }
);

AccordionRoot.displayName = 'AccordionRoot';

// Accordion Item Component
export interface AccordionItemProps extends BoxProps {
  value: string;
  disabled?: boolean;
  children: React.ReactNode;
}

const AccordionItem = React.forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ value, disabled = false, children, ...props }, ref) => {
    const { variant } = useAccordionContext();

    const variantStyles = {
      default: {
        borderBottom: '1px solid #E2E8F0',
      },
      bordered: {
        border: 'none',
      },
      filled: {
        border: 'none',
      },
      separated: {
        border: '1px solid #E2E8F0',
        borderRadius: 'lg',
        overflow: 'hidden',
      },
    };

    return (
      <AccordionItemContext.Provider value={value}>
        <Box
          ref={ref}
          opacity={disabled ? 0.6 : 1}
          pointerEvents={disabled ? 'none' : 'auto'}
          {...variantStyles[variant]}
          {...props}
        >
          {children}
        </Box>
      </AccordionItemContext.Provider>
    );
  }
);

AccordionItem.displayName = 'AccordionItem';

// Accordion Item Trigger Component
export interface AccordionItemTriggerProps extends BoxProps {
  children: React.ReactNode;
}

const AccordionItemTrigger = React.forwardRef<HTMLDivElement, AccordionItemTriggerProps>(
  ({ children, ...props }, ref) => {
    const { multiple, value, onValueChange, variant, size } = useAccordionContext();
    const itemValue = React.useContext(AccordionItemContext);
    const open = value.includes(itemValue);

    const handleClick = () => {
      let newValue: string[];
      if (multiple) {
        if (open) {
          newValue = value.filter((v) => v !== itemValue);
        } else {
          newValue = [...value, itemValue];
        }
      } else {
        newValue = open ? [] : [itemValue];
      }
      onValueChange(newValue);
    };

    const sizeConfig = {
      sm: {
        padding: 3,
        fontSize: 'sm',
      },
      md: {
        padding: 4,
        fontSize: 'md',
      },
      lg: {
        padding: 6,
        fontSize: 'lg',
      },
    };

    const config = sizeConfig[size];

    const variantStyles = {
      default: {
        backgroundColor: 'transparent',
        _hover: { backgroundColor: '#F7FAFC' },
      },
      bordered: {
        backgroundColor: 'transparent',
        _hover: { backgroundColor: '#F7FAFC' },
      },
      filled: {
        backgroundColor: open ? 'white' : 'transparent',
        _hover: { backgroundColor: open ? 'white' : '#EDF2F7' },
      },
      separated: {
        backgroundColor: 'transparent',
        _hover: { backgroundColor: '#F7FAFC' },
      },
    };

    return (
      <Box
        ref={ref as React.Ref<HTMLDivElement>}
        as="button"
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        textAlign="left"
        border="none"
        cursor="pointer"
        transition="all 0.2s"
        padding={config.padding}
        fontSize={config.fontSize}
        fontWeight="medium"
        onClick={handleClick}
        {...variantStyles[variant]}
        {...props}
      >
        <Box flex={1}>{children}</Box>
        <AccordionItemIndicator />
      </Box>
    );
  }
);

AccordionItemTrigger.displayName = 'AccordionItemTrigger';

// Accordion Item Indicator Component
export type AccordionItemIndicatorProps = BoxProps;

const AccordionItemIndicator = React.forwardRef<HTMLDivElement, AccordionItemIndicatorProps>(
  ({ ...props }, ref) => {
    const { value, iconType, iconPosition } = useAccordionContext();
    const itemValue = React.useContext(AccordionItemContext);
    const open = value.includes(itemValue);

    const getIcon = () => {
      switch (iconType) {
        case 'caret':
          return <CaretDownIcon />;
        case 'caret-right':
          return <CaretRightIcon />;
        case 'plus':
          return open ? <Cross1Icon /> : <PlusIcon />;
        case 'arrow':
          return <ArrowDownIcon />;
        default:
          return <CaretDownIcon />;
      }
    };

    const getRotation = () => {
      if (iconType === 'caret-right') {
        return open ? '90deg' : '0deg';
      }
      if (iconType === 'caret' || iconType === 'arrow') {
        return open ? '180deg' : '0deg';
      }
      return '0deg';
    };

    return (
      <Box
        ref={ref}
        marginLeft={iconPosition === 'right' ? 2 : 0}
        marginRight={iconPosition === 'left' ? 2 : 0}
        transform={`rotate(${getRotation()})`}
        transition="transform 0.2s"
        {...props}
      >
        <Icon size="xl" iconColor={open ? 'primary' : 'muted'}>
          {getIcon()}
        </Icon>
      </Box>
    );
  }
);

AccordionItemIndicator.displayName = 'AccordionItemIndicator';

// Accordion Item Content Component
export interface AccordionItemContentProps extends BoxProps {
  children: React.ReactNode;
}

const AccordionItemContent = React.forwardRef<HTMLDivElement, AccordionItemContentProps>(
  ({ children, ...props }, ref) => {
    const { value } = useAccordionContext();
    const itemValue = React.useContext(AccordionItemContext);
    const open = value.includes(itemValue);

    if (!open) {
      return null;
    }

    return (
      <Box ref={ref} {...props}>
        {children}
      </Box>
    );
  }
);

AccordionItemContent.displayName = 'AccordionItemContent';

// Accordion Item Body Component
export interface AccordionItemBodyProps extends BoxProps {
  children: React.ReactNode;
}

const AccordionItemBody = React.forwardRef<HTMLDivElement, AccordionItemBodyProps>(
  ({ children, ...props }, ref) => {
    const { size } = useAccordionContext();

    const sizeConfig = {
      sm: {
        padding: 3,
        paddingTop: 0,
      },
      md: {
        padding: 4,
        paddingTop: 0,
      },
      lg: {
        padding: 6,
        paddingTop: 0,
      },
    };

    const config = sizeConfig[size];

    return (
      <Box
        ref={ref}
        padding={config.padding}
        paddingTop={config.paddingTop}
        lineHeight="1.6"
        color="#4A5568"
        {...props}
      >
        {children}
      </Box>
    );
  }
);

AccordionItemBody.displayName = 'AccordionItemBody';

// Compound component exports
export const Accordion = {
  Root: AccordionRoot,
  Item: AccordionItem,
  ItemTrigger: AccordionItemTrigger,
  ItemIndicator: AccordionItemIndicator,
  ItemContent: AccordionItemContent,
  ItemBody: AccordionItemBody,
};

// Individual exports for direct usage
export {
  AccordionRoot,
  AccordionItem,
  AccordionItemTrigger,
  AccordionItemIndicator,
  AccordionItemContent,
  AccordionItemBody,
};

// Legacy accordion with simpler API for backwards compatibility
export interface LegacyAccordionProps extends Omit<AccordionRootProps, 'children'> {
  items?: Array<{
    value: string;
    title: string;
    content: React.ReactNode;
    icon?: React.ReactNode;
    disabled?: boolean;
  }>;
  children?: React.ReactNode;
}

export const LegacyAccordion = React.forwardRef<HTMLDivElement, LegacyAccordionProps>(
  ({ items = [], children, ...props }, ref) => {
    if (items.length > 0) {
      return (
        <AccordionRoot ref={ref} {...props}>
          {items.map((item: (typeof items)[number]) => (
            <AccordionItem key={item.value} value={item.value} disabled={item.disabled}>
              <AccordionItemTrigger>
                {item.icon}
                {item.title}
              </AccordionItemTrigger>
              <AccordionItemContent>
                <AccordionItemBody>{item.content}</AccordionItemBody>
              </AccordionItemContent>
            </AccordionItem>
          ))}
        </AccordionRoot>
      );
    }

    return (
      <AccordionRoot ref={ref} {...props}>
        {children}
      </AccordionRoot>
    );
  }
);

LegacyAccordion.displayName = 'LegacyAccordion';

export default LegacyAccordion;
